/**
 * `/connectors` — the twenty-one source systems.
 *
 * The port of the demo's connector grid (`CONN` at demo line 2456, rendered at
 * demo line 2479). One card per system: its glyph, its name and the dimmed
 * qualifier after it, a freshness pill, what it feeds, and two or three stat
 * chips carrying this morning's figures.
 *
 * Clicking a card opens the data browser filtered to that connector's folder,
 * which the demo could not do — its connector cards were inert. The whole
 * section argues that a system's own export is enough to be a source, and the
 * shortest proof of that is the files themselves.
 *
 * ## Data
 *
 * The stat chips are live expressions over eight lazy domains plus the lake
 * index — see `OemConnectorsScene.transform.ts` for why the page pays that
 * cost and what it does when a domain has not landed.
 *
 * @see src/versions/v1/modules/integration/OemConnectorsScene.transform.ts
 * @see src/versions/v1/shared/seed/connectors.ts
 * @see docs/logic/integration/connectors.md
 */

import { useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { useOemData } from '../../shared/useOemData';
import { useLakeIndex } from './useLakeIndex';
import { FORMATS_NOTE } from './IntegrationSectionScene.transform';
import { SeedGlyph } from './SeedGlyph';
import {
  connectorCards,
  connectorCountChip,
  type ConnectorFigureCtx,
} from './OemConnectorsScene.transform';

/** Demo line 1452 — the view standfirst, verbatim. */
const STANDFIRST =
  "The systems the workspace links into — the OEM's own, the customers' and the third " +
  "parties'. Each card says what it feeds and how fresh it is. The point is that any system " +
  'with an export, an API or a mailbox can be a source.';

/** The card's dom id, so `?conn=<name>` can scroll to it. Mirrors `ag-<name>`. */
function connectorDomId(name: string): string {
  return `conn-${name.replace(/\W/g, '')}`;
}

export function OemConnectorsScene() {
  // fork(QA2): a connector chip on Asset360 arrives as `?conn=<name>`, the same
  // shape the agent strip uses for `?agent=`, so the strip lands on the card it
  // named rather than on the top of the grid.
  const [search] = useSearchParams();
  const selected = search.get('conn');

  const service = useOemData('service');
  const parts = useOemData('parts');
  const people = useOemData('people');
  const rms = useOemData('rms');
  const comms = useOemData('comms');
  const docIndex = useOemData('doc-index');
  const telemetry = useOemData('telemetry');
  const workOrders = useOemData('work-orders');
  const lake = useLakeIndex();

  const ctx = useMemo<ConnectorFigureCtx>(
    () => ({
      service: service.data ?? undefined,
      parts: parts.data ?? undefined,
      people: people.data ?? undefined,
      rms: rms.data ?? undefined,
      comms: comms.data ?? undefined,
      docIndex: docIndex.data ?? undefined,
      oil: telemetry.data?.oil,
      tyres: telemetry.data?.tyres,
      workOrders: workOrders.data ?? undefined,
      lake: lake.index ?? undefined,
    }),
    [
      comms.data,
      docIndex.data,
      lake.index,
      parts.data,
      people.data,
      rms.data,
      service.data,
      telemetry.data,
      workOrders.data,
    ],
  );

  const cards = useMemo(() => connectorCards(ctx), [ctx]);

  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selected || !listRef.current) return;
    const target = listRef.current.querySelector(`#${CSS.escape(connectorDomId(selected))}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selected]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="OemConnectorsScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Integration"
            title="Connectors"
            description={STANDFIRST}
            actions={
              <div className="flex items-center gap-2">
                <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                  {connectorCountChip()}
                </span>
                <HelpAnchor doc="integration/connectors" />
              </div>
            }
          />

          <AskStrip view="connectors" />

          <Commentable name="ConnectorCards">
            <div
              ref={listRef}
              className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3"
            >
              {cards.map(({ connector, stats, to }) => {
                const isSelected = selected === connector.name;
                return (
                <Link
                  key={connector.name}
                  id={connectorDomId(connector.name)}
                  to={to}
                  title={`Open ${connector.name} in the data browser`}
                  className={cn(
                    'group flex min-w-0 flex-col gap-2 rounded-xl border p-4 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
                    isSelected ? 'ring-2' : '',
                  )}
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--surface-2)',
                    ...(isSelected ? { boxShadow: '0 0 0 2px var(--accent)' } : {}),
                  }}
                >
                  <span className="flex items-start gap-2.5">
                    <span
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                      style={{ background: 'var(--surface-3)', color: 'var(--accent)' }}
                    >
                      <SeedGlyph icon={connector.icon} size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-[13.5px] font-semibold leading-tight tracking-tight"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {connector.name}
                      </span>
                      <span
                        className="mt-0.5 block text-[11px] leading-snug"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        {connector.subtitle}
                      </span>
                    </span>
                    <span className={`band-pill shrink-0 ${connector.tone === 'good' ? 'band-good' : 'band-fair'}`}>
                      {connector.status}
                    </span>
                  </span>

                  <span
                    className="text-[11.5px] leading-[1.55]"
                    style={{ color: 'var(--ink-secondary)' }}
                  >
                    {connector.description}
                  </span>

                  {stats.length > 0 ? (
                    <span className="mt-auto flex flex-wrap gap-1.5 pt-1">
                      {stats.map((stat) => (
                        <span
                          key={stat.key}
                          className="inline-flex items-baseline gap-0.5 rounded-full px-2 py-px text-[10.5px] leading-[1.6]"
                          style={{ background: 'var(--surface-3)', color: 'var(--ink-secondary)' }}
                        >
                          {stat.lead ? (
                            <b className="mono" style={{ color: 'var(--ink-primary)' }}>
                              {stat.lead}
                            </b>
                          ) : null}
                          <span>{stat.text}</span>
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span
                      aria-hidden="true"
                      className="mt-auto block h-[18px] w-2/3 animate-pulse rounded-full"
                      style={{ background: 'var(--surface-3)' }}
                    />
                  )}
                </Link>
                );
              })}
            </div>
          </Commentable>

          <Commentable name="ConnectorFormatsNote">
            <p className="text-[11.5px] leading-[1.6]" style={{ color: 'var(--ink-tertiary)' }}>
              {FORMATS_NOTE}
            </p>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
